import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GiftIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FidelityRewards = () => {
  const { token } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_required: '',
    image_url: '',
    available_quantity: '',
    is_active: true
  });

  useEffect(() => {
    loadRewards();
  }, []);

  const loadRewards = async () => {
    try {
      const response = await axios.get(`${API}/fidelity/rewards?active_only=false`);
      setRewards(response.data);
    } catch (error) {
      console.error('Errore caricamento premi:', error);
      toast.error('Errore nel caricamento dei premi');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      points_required: '',
      image_url: '',
      available_quantity: '',
      is_active: true
    });
    setEditingReward(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      points_required: parseInt(formData.points_required),
      available_quantity: parseInt(formData.available_quantity)
    };

    try {
      if (editingReward) {
        await axios.put(`${API}/fidelity/rewards/${editingReward.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Premio aggiornato con successo');
      } else {
        await axios.post(`${API}/fidelity/rewards`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Premio creato con successo');
      }
      
      setIsModalOpen(false);
      resetForm();
      await loadRewards();
    } catch (error) {
      console.error('Errore salvataggio premio:', error);
      toast.error('Errore nel salvataggio del premio');
    }
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      points_required: reward.points_required.toString(),
      image_url: reward.image_url || '',
      available_quantity: reward.available_quantity.toString(),
      is_active: reward.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo premio?')) {
      return;
    }

    try {
      await axios.delete(`${API}/fidelity/rewards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Premio eliminato con successo');
      await loadRewards();
    } catch (error) {
      console.error('Errore eliminazione premio:', error);
      toast.error('Errore nell\'eliminazione del premio');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento premi...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalogo Premi</h1>
          <p className="text-gray-600">Gestione premi riscattabili con punti</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Nuovo Premio
        </Button>
      </div>

      {/* Rewards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rewards.map((reward) => (
          <Card key={reward.id} className={!reward.is_active ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg">{reward.name}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <Badge variant={reward.is_active ? "default" : "secondary"}>
                      {reward.is_active ? "Attivo" : "Disattivato"}
                    </Badge>
                    <Badge variant="outline">
                      {reward.points_required} punti
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(reward)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <PencilIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(reward.id)}
                    className="p-2 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {reward.image_url && (
                <img
                  src={reward.image_url}
                  alt={reward.name}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <CardDescription className="mb-4">
                {reward.description}
              </CardDescription>
              <div className="text-sm text-gray-600">
                Quantità disponibile: <strong>{reward.available_quantity}</strong>
              </div>
            </CardContent>
          </Card>
        ))}

        {rewards.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-16 text-center">
              <GiftIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun premio nel catalogo
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Crea il primo premio per iniziare
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Crea Premio
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>
                {editingReward ? 'Modifica Premio' : 'Nuovo Premio'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Premio *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="es. Bottiglia Amaro Milone"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione *</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Descrizione dettagliata del premio"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[100px]"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="points">Punti Richiesti *</Label>
                    <Input
                      id="points"
                      type="number"
                      value={formData.points_required}
                      onChange={(e) => setFormData({...formData, points_required: e.target.value})}
                      placeholder="100"
                      min="1"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantità Disponibile *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      value={formData.available_quantity}
                      onChange={(e) => setFormData({...formData, available_quantity: e.target.value})}
                      placeholder="50"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">URL Immagine (opzionale)</Label>
                  <Input
                    id="image"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="active"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="active" className="cursor-pointer">
                    Premio attivo (visibile ai clienti)
                  </Label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingReward ? 'Aggiorna' : 'Crea Premio'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                  >
                    Annulla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FidelityRewards;
